import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../../services/api';
import {
  getScholarshipLineups,
  updateScholarshipLineup,
} from '../../../../services/scholarships';
import type {
  LineupSlot,
  LineupVersus,
  ScholarshipLineups,
} from '../../../../types/scholarships';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { LINEUP_SIDES, LINEUP_TITLES, lineupKey } from './lineups';

export const LINEUP_CONFLICT_MESSAGE =
  'This lineup changed while it was saving, so it has been reloaded. Check it and make your change again.';

export const LINEUP_UNKNOWN_PLAYER_MESSAGE =
  'A player in this lineup is no longer on the board, so the board has been reloaded. Check it and make your change again.';

type PerSide<T> = Record<LineupVersus, T>;

export interface LineupsState {
  lineups: ScholarshipLineups | null;
  error: string | null;
  /** The whole next lineup for one side. Shown at once, saved in order (see below). */
  change: (side: LineupVersus, slots: LineupSlot[]) => void;
}

/**
 * The two batting orders and their saves.
 *
 * Each side has at most ONE save in flight. The PUT replaces the whole side and the API answers a
 * second, overlapping replace with a 409, so two quick edits sent together meant the newer one was
 * refused and lost. A change made while a save is running is shown at once and queued; only the
 * latest queued lineup is kept (it already contains the earlier edits), and it is sent when the
 * running save settles. The two sides are independent.
 *
 * A reload after a refused save is guarded by a per-side edit counter, so it only writes a side
 * the coach has not changed since the reload began, and never a side with a save in flight.
 *
 * `onUnknownPlayers` runs on a 404 (a player named in the lineup has left the board), so the
 * caller can refetch the players too; otherwise the same stale row would be sent again forever.
 */
export const useLineups = (
  scholarshipId: string,
  onUnknownPlayers?: () => void,
): LineupsState => {
  const [lineups, setLineups] = useState<ScholarshipLineups | null>(null);
  const [error, setError] = useState<string | null>(null);

  const edits = useRef<PerSide<number>>({ lhp: 0, rhp: 0 });
  const inFlight = useRef<PerSide<boolean>>({ lhp: false, rhp: false });
  const queued = useRef<PerSide<LineupSlot[] | null>>({ lhp: null, rhp: null });
  // What the server last confirmed per side, for the rollback after a failed save.
  const confirmed = useRef<PerSide<LineupSlot[]>>({ lhp: [], rhp: [] });

  const setSide = useCallback((side: LineupVersus, slots: LineupSlot[]) => {
    const key = lineupKey(side);
    setLineups((current) => (current ? { ...current, [key]: slots } : current));
  }, []);

  const load = useCallback(async () => {
    try {
      const loaded = await getScholarshipLineups(scholarshipId);
      confirmed.current = { lhp: loaded.vsLhp, rhp: loaded.vsRhp };
      setLineups(loaded);
    } catch (e) {
      setError(getSafeErrorMessage(e, 'Failed to load lineups'));
    }
  }, [scholarshipId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Refetch and write back only the given sides the coach has left alone meanwhile. */
  const reloadSides = useCallback(
    async (sides: readonly LineupVersus[]) => {
      const startedAt = { ...edits.current };
      try {
        const loaded = await getScholarshipLineups(scholarshipId);
        for (const side of sides) {
          if (edits.current[side] !== startedAt[side] || inFlight.current[side]) continue;
          const slots = loaded[lineupKey(side)];
          confirmed.current[side] = slots;
          setSide(side, slots);
        }
      } catch (e) {
        // The reload was meant to replace a lineup the server refused; without it, put back what
        // the server last confirmed rather than leave the refused one up. Appended, so the reason
        // for the jump back stays on screen.
        for (const side of sides) {
          if (edits.current[side] !== startedAt[side] || inFlight.current[side]) continue;
          setSide(side, confirmed.current[side]);
        }
        const message = getSafeErrorMessage(e, 'Failed to load lineups');
        setError((current) => (current ? `${current} ${message}` : message));
      }
    },
    [scholarshipId, setSide],
  );

  const send = useCallback(
    async (side: LineupVersus, slots: LineupSlot[]) => {
      inFlight.current[side] = true;
      let outgoing: LineupSlot[] | null = slots;
      while (outgoing) {
        try {
          const saved = await updateScholarshipLineup(scholarshipId, side, outgoing);
          confirmed.current[side] = saved[lineupKey(side)];
          outgoing = queued.current[side];
          queued.current[side] = null;
          // Nothing newer waiting: the server's copy is what the coach last asked for.
          if (!outgoing) setSide(side, confirmed.current[side]);
        } catch (e) {
          // A queued lineup was built on the one just refused; sending it would repeat the failure
          // or quietly re-add what the server turned down.
          queued.current[side] = null;
          outgoing = null;
          inFlight.current[side] = false;
          const title = LINEUP_TITLES[side];
          if (e instanceof ApiError && e.statusCode === 409) {
            setError(`${title}: ${LINEUP_CONFLICT_MESSAGE}`);
            reloadSides([side]);
          } else if (e instanceof ApiError && e.statusCode === 404) {
            setError(`${title}: ${LINEUP_UNKNOWN_PLAYER_MESSAGE}`);
            reloadSides(LINEUP_SIDES);
            onUnknownPlayers?.();
          } else {
            setSide(side, confirmed.current[side]);
            setError(getSafeErrorMessage(e, `Failed to save the lineup ${title}`));
          }
          return;
        }
      }
      inFlight.current[side] = false;
    },
    [scholarshipId, setSide, reloadSides, onUnknownPlayers],
  );

  const change = useCallback(
    (side: LineupVersus, slots: LineupSlot[]) => {
      edits.current[side] += 1;
      setSide(side, slots);
      setError(null);
      if (inFlight.current[side]) {
        queued.current[side] = slots;
        return;
      }
      send(side, slots);
    },
    [send, setSide],
  );

  return { lineups, error, change };
};
